import { cloneDeep } from "lodash";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useAlert } from "react-alert";
import ReactModal from "react-modal";
import {
  AddIcon,
  EditIcon,
  ErrorRoundedFilledIcon,
  RefreshIcon,
  TrashIcon,
} from "../components/icons/themes/windows10";
import useOnClickOutside from "../hooks/on-click-outside";
import { useOnPressEnter } from "../hooks/on-key-press";
import { EditableEnvironment, EditableProject } from "../reducers/environments";

const modalStyles = {
  content: {
    zIndex: 10050,
    minHeight: `100px`,
    width: 600,
    top: "auto",
    bottom: "auto",
    right: "auto",
    left: "auto",
    position: "static" as "static",
    display: "flex",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, .8)",
    zIndex: 10000,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};
export type ModalState = {
  title: string;
  placeholder: string;
  project: EditableProject;
  closeOnOverlay: boolean;
  addingNewProject?: boolean;
  error?: string;
  addingNewEnvironment?: boolean;
  onClose?: () => void;
  onSave: (updatedProject: EditableProject) => void;
  isLoading: boolean;
};
/**
 * A single project row in the table of projects
 * @param {() => void} removeEnvironment
 * @param {(name: string) => void} updateEnvironmentName
 * @param {EnvironmentBase & {projectID: ProjectID, deletable: boolean}} environment
 * @returns {JSX.Element}
 * @constructor
 */
const EditProjectModalEnvironmentRow: React.FC<{
  environment: EditableEnvironment;
  updateEnvironmentName: (name: string) => void;
  removeEnvironment: () => void;
}> = ({ removeEnvironment, updateEnvironmentName, environment }) => {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);

  const startEditing = () => {
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const handleDelete = (e: React.MouseEvent<SVGElement>) => {
    e.stopPropagation();
    e.preventDefault();
    removeEnvironment();
    setIsEditing(false);
  };

  useEffect(() => {
    if (isEditing && inputRef && inputRef.current) {
      // @ts-ignore
      inputRef.current.focus();
      // @ts-ignore
      inputRef.current.select();
    }
  }, [inputRef, isEditing]);

  useOnClickOutside([inputRef], cancelEditing);

  return (
    <>
      {!isEditing && (
        <div className="flex items-center justify-between">
          <div
            tabIndex={0}
            onFocus={startEditing}
            className="text-sm color-gray-800 py-3 px-2 cursor-text"
            onClick={startEditing}
          >
            {environment.name ? (
              environment.name
            ) : (
              <div className="text-red-300">Missing Environment Name...</div>
            )}
          </div>
          <div>
            <EditIcon className="cursor-pointer mr-2" onClick={startEditing} />
            {environment.deletable && (
              <TrashIcon
                className="cursor-pointer fill-red-600 mr-2"
                onClick={handleDelete}
              />
            )}
          </div>
        </div>
      )}

      {isEditing && (
        <div className="flex items-center justify-between">
          <input
            placeholder="Environment name..."
            tabIndex={0}
            onBlur={cancelEditing}
            ref={inputRef}
            value={environment.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              updateEnvironmentName(e.target.value)
            }
            className=" w-full text-sm my-1 bg-gray-200 color-gray-800 p-2"
          />
        </div>
      )}
    </>
  );
};
export const EditProjectModal: React.FC<ModalState> = ({
  addingNewEnvironment = false,
  addingNewProject = false,
  project,
  placeholder,
  onSave,
  title,
  onClose,
  closeOnOverlay,
  error,
  isLoading,
}) => {
  const alert = useAlert();
  const [addingNewEnv, setAddingNewEnv] = useState(addingNewEnvironment);
  const [newEnvironmentName, setNewEnvironmentName] = useState("");
  const addingNewRef = useRef(null);
  const projectNameRef = useRef(null);
  const [newProjectState, setNewProjectState] = useState(cloneDeep(project));

  const maybeCancelAddingNewEnvironment = () => {
    if (!newEnvironmentName) {
      setAddingNewEnv(false);
    } else {
      saveNewEnv();
    }
  };

  const onConfirm = () => {
    onSave(newProjectState);
  };

  const saveNewEnv = () => {
    // Add a new environment to the list
    const envsCopy = [...newProjectState.environments];
    envsCopy.push({
      accountID: newProjectState.accountID,
      projectID: newProjectState.ID,
      ID: null,
      name: newEnvironmentName,
      deletable: true,
    });
    setNewProjectState({ ...newProjectState, environments: envsCopy });
    setNewEnvironmentName("");
    setAddingNewEnv(false);
  };

  const focusAddNewField = () => {
    // Hack: Need a little timeout
    setTimeout(() => {
      if (addingNewEnv && addingNewRef && addingNewRef.current) {
        // @ts-ignore
        addingNewRef.current.focus();
        // @ts-ignore
        addingNewRef.current.select();
      } else if (addingNewProject && projectNameRef && projectNameRef.current) {
        // @ts-ignore
        projectNameRef.current.focus();
        // @ts-ignore
        projectNameRef.current.select();
      }
    }, 1);
  };

  useEffect(focusAddNewField, [addingNewEnv, addingNewRef]);
  useOnClickOutside([addingNewRef], maybeCancelAddingNewEnvironment);
  useOnPressEnter([addingNewRef], () => {
    if (newEnvironmentName) {
      saveNewEnv();
    }
  });

  const updateEnvironmentName = (envIndex: number, name: string) => {
    const envsCopy = cloneDeep(newProjectState.environments);
    envsCopy[envIndex].name = name;
    setNewProjectState({ ...newProjectState, environments: envsCopy });
  };

  const removeEnvironment = (envIndex: number) => {
    // Only remove deletable environments.
    if (newProjectState.environments[envIndex].deletable) {
      const envsCopy = [...newProjectState.environments];
      envsCopy.splice(envIndex, 1);
      setNewProjectState({ ...newProjectState, environments: envsCopy });
    } else {
      alert.error("You can not delete this environment");
    }
  };

  return (
    <ReactModal
      // This silences a warning in the console from react-modal
      appElement={document.getElementById("root") as HTMLElement}
      isOpen={true}
      onRequestClose={closeOnOverlay ? onClose : () => {}}
      style={modalStyles}
      contentLabel={title}
    >
      <div className="flex flex-col justify-between w-full">
        <div>
          <h2 className="text-xl text-gray-900">{title}</h2>

          <p className="mt-6">
            <span className="text-sm text-gray-700">Name:</span>
            <input
              tabIndex={0}
              className="border-b border-b-gray-800 focus:bg-gray-200 focus:border-b-transparent color-gray-800 w-full mt-1 p-2"
              placeholder={`${placeholder} name...`}
              type="text"
              ref={projectNameRef}
              value={newProjectState.name}
              onChange={(e) =>
                setNewProjectState({ ...newProjectState, name: e.target.value })
              }
            />
          </p>
        </div>

        {/* Environments */}
        <div>
          <h3 className="text-md text-gray-800 font-bold mt-8">
            Environments:
          </h3>
          {newProjectState.environments.map(
            (e: EditableEnvironment, index: number) => (
              <EditProjectModalEnvironmentRow
                removeEnvironment={() => removeEnvironment(index)}
                updateEnvironmentName={(name) =>
                  updateEnvironmentName(index, name)
                }
                key={index}
                environment={e}
              />
            )
          )}

          {addingNewEnv && (
            <div className="flex items-center justify-between">
              <input
                tabIndex={0}
                placeholder="New Environment name..."
                ref={addingNewRef}
                value={newEnvironmentName}
                onChange={(e) => setNewEnvironmentName(e.target.value)}
                onBlur={maybeCancelAddingNewEnvironment}
                className="text-sm mb-1 mt-2 bg-gray-200 color-gray-800 p-2 w-full "
              />
            </div>
          )}

          {!addingNewEnv && (
            <div className="flex justify-center">
              <button
                tabIndex={0}
                onClick={() => setAddingNewEnv(true)}
                className="flex mt-3 rounded color-purple-900 bg-tranparent text-normal hover:bg-purple-200 focus:bg-purple-200 text-center w-1/3 mx-auto px-3 py-2 justify-center items-center"
              >
                <AddIcon className="mr-2" />
                Add New
              </button>
            </div>
          )}
        </div>

        <div className="mt-8">
          {/* ERROR MESSAGE */}
          {error && (
            <div className="px-3 mb-3 bg-red-500 text-white py-2 font-bold text-lg flex items-center rounded">
              <ErrorRoundedFilledIcon className="fill-white mr-2" />
              {error}
            </div>
          )}

          {isLoading && (
            <div className="px-3 mb-3 py-2 font-bold text-lg flex items-center rounded">
              <RefreshIcon className="animate-spin mx-auto" />
            </div>
          )}

          <div className="flex space-between">
            <button
              className="w-full rounded btn py-2 bg-red-200 text-red-900 hover:bg-red-300 text-center "
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <div className="w-4 flex-shrink-0 flex-grow-0" />
            <button
              className="rounded w-full btn py-2 text-center bg-purple-200 text-purple-900 hover:bg-purple-300"
              onClick={onConfirm}
              disabled={isLoading}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </ReactModal>
  );
};
